import {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useLayoutEffect,
  useRef,
} from 'react';
import SliderComponent from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { useSliderParamsGenerator } from '@/hooks';
import { NextArrow, PrevArrow } from '@/components/buttons/SliderButtons';

const defaultSettings = {
  infinite: true,
  autoplay: true,
  speed: 1000,
  slidesToShow: 2,
  slidesToScroll: 1,
};

interface SliderProps {
  settings?: { [key: string]: string | number | boolean | ReactNode };
  children: ReactNode[];
  className?: string;
  width: number;
  withArrows?: boolean;
  tabletShowSlider?: number;
  desktopShowSlider?: number;
  mobShowSlider?: number;
  setHeight?: Dispatch<SetStateAction<number>>;
  calculateHeight?: boolean;
  infinite?: boolean;
}

export const Slider: FC<SliderProps> = ({
  settings = defaultSettings,
  children,
  className,
  width = 280,
  withArrows = false,
  tabletShowSlider,
  desktopShowSlider,
  mobShowSlider,
  setHeight,
  calculateHeight = false,
}) => {
  const implementedSettings = {
    ...defaultSettings,
    ...settings,
    slidesToShow: useSliderParamsGenerator(
      children.length,
      width,
      tabletShowSlider,
      desktopShowSlider,
      mobShowSlider,
    ),
  };
  const sliderRef = useRef<SliderComponent>(null);

  const handlePrevClick = () => {
    sliderRef.current?.slickPrev();
  };

  const handleNextClick = () => {
    sliderRef.current?.slickNext();
  };

  useLayoutEffect(() => {
    const height = sliderRef.current?.innerSlider?.list?.offsetHeight;
    if (height && setHeight && calculateHeight) {
      setHeight(height);
    }
  }, [setHeight, calculateHeight]);

  const sliderSettings = withArrows
    ? {
        prevArrow: <PrevArrow onClick={handlePrevClick} />,
        nextArrow: <NextArrow onClick={handleNextClick} />,
      }
    : {};

  return (
    <>
      {withArrows && children.length > implementedSettings.slidesToShow && (
        <div className="custom-slider-arrows mb-8 flex justify-end gap-7">
          {sliderSettings.prevArrow}
          {sliderSettings.nextArrow}
        </div>
      )}
      <SliderComponent
        ref={sliderRef}
        className={className}
        {...implementedSettings}
      >
        {children}
      </SliderComponent>
    </>
  );
};
